import React, { useEffect, useMemo, useState, useTransition } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ScheduleHeader from "../../../App/ScheduleHeader";
import ScheduleNotes from "../../ScheduleNotes";
import ScheduleContainer from "../../utils/ScheduleContainer";
import {
  createClaimLine,
  initializeSharedBasedPaymentData,
  removeClaimLine,
  renumberClaimLines,
  updateClaimLine,
} from "./sharedBasedPaymentData";
import SectionTabber from "../../utils/SectionTabber";
import SharesCostTracker from "./SharesCostTracker";
import QualifyingDeductionTable from "./QualifyingDeductionTable";
import { calculateSharedBasedPayment } from "./sharedBasedPaymentCalculation";
import SharedBasedPaymentExcelUpload from "./SharedBasedPaymentExcelUpload";
import FurtherDeductionOutput from "./FurtherDeductionOutput";
import SharedBasedPaymentPdf from "../Pdf/SharedBasedPaymentPdf";
import SharedBasedPaymentValidationSummary from "./SharedBasedPaymentValidationSummary";
import ValidationWarningDialog from "../../../utils/dialog/ValidationWarningDialog";

const createScheduleDetails = (data, calculation, notes, reviewNotes) => ({
  ...data,
  totalAdjustment: calculation.output.qualifyingDeductionSgd,
  doc: JSON.stringify({ notes, reviewNotes }),
});

const SharedBasedPaymentSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [schName, setSchName] = useState("Share-Based Payment");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [scheduleData, setScheduleData] = useState(null);
  const [requestedTrackerState, setRequestedTrackerState] = useState(null);
  const [isTrackerTransitionPending, startTrackerTransition] = useTransition();
  const trackerLoading =
    requestedTrackerState !== null || isTrackerTransitionPending;
  const [activeTab, setActiveTab] = useState("tracker");
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [excelImportSummary, setExcelImportSummary] = useState(null);
  const [validationWarning, setValidationWarning] = useState({
    open: false,
    pendingSave: null,
    loading: false,
  });
  const calculationResults = useMemo(
    () => calculateSharedBasedPayment(scheduleData || {}),
    [scheduleData]
  );
  const saveValidationWarnings = useMemo(() => {
    const errors = Array.isArray(calculationResults.validation?.errors)
      ? calculationResults.validation.errors
      : [];
    const claimLineIndex = new Map(
      (scheduleData?.claimLines || []).map((line, index) => [
        line.claimLineId,
        line.lineId ||
          line.description?.trim() ||
          `Deduction line ${index + 1}`,
      ])
    );
    const transactionIndex = new Map(
      (scheduleData?.transactions || []).map((transaction, index) => [
        transaction.transactionId,
        transaction.reference?.trim() ||
          transaction.date ||
          `Tracker transaction ${index + 1}`,
      ])
    );

    return errors.map((error) => ({
      title:
        claimLineIndex.get(error.scope) ||
        transactionIndex.get(error.scope) ||
        "Schedule",
      description: error.message,
    }));
  }, [calculationResults.validation?.errors, scheduleData]);

  useEffect(() => {
    const nextName = schedule?.name || "Share-Based Payment";
    const nextNotes = schedule?.notes || "";
    const nextReviewNotes = schedule?.reviewNotes || "";
    const initializedData = initializeSharedBasedPaymentData(scheduleDetails, {
      scheduleId: schedule?.id || "",
      yearOfAssessment: company?.yearOfAssessment || "",
      financialYearStart: company?.financialYearStart || "",
      financialYearEnd: company?.financialYearEnd || "",
    });

    setActiveTab("tracker");
    setExcelDialogOpen(false);
    setExcelImportSummary(null);
    setValidationWarning({
      open: false,
      pendingSave: null,
      loading: false,
    });
    setSchName(nextName);
    setNotes(nextNotes);
    setReviewNotes(nextReviewNotes);
    setScheduleData(initializedData);
    setRequestedTrackerState(null);
  }, [
    company?.financialYearEnd,
    company?.financialYearStart,
    company?.yearOfAssessment,
    schedule?.id,
    schedule?.name,
    schedule?.notes,
    schedule?.reviewNotes,
    scheduleDetails,
  ]);

  useEffect(() => {
    if (requestedTrackerState === null) return undefined;
    if (
      !canEditSchedule ||
      scheduleData?.useCostTracker === requestedTrackerState
    ) {
      // The updated data and its derived calculation have committed.
      setRequestedTrackerState(null);
      return undefined;
    }

    // Give the busy state a paint before starting the synchronous calculation.
    let calculationFrame;
    const paintFrame = window.requestAnimationFrame(() => {
      calculationFrame = window.requestAnimationFrame(() => {
        startTrackerTransition(() => {
          setScheduleData((current) =>
            current
              ? { ...current, useCostTracker: requestedTrackerState }
              : current
          );
        });
      });
    });

    return () => {
      window.cancelAnimationFrame(paintFrame);
      if (calculationFrame !== undefined) {
        window.cancelAnimationFrame(calculationFrame);
      }
    };
  }, [
    requestedTrackerState,
    scheduleData?.useCostTracker,
    canEditSchedule,
    startTrackerTransition,
  ]);

  const handleTrackerToggle = (checked) => {
    if (!canEditSchedule || !scheduleData || trackerLoading) return;
    if (Boolean(scheduleData.useCostTracker) === checked) return;
    setRequestedTrackerState(checked);
  };

  const createAndAddClaim = () => {
    if (!canEditSchedule || !scheduleData) return null;
    const newLine = createClaimLine(scheduleData);
    const nextData = {
      ...scheduleData,
      claimLines: [...scheduleData.claimLines, newLine],
    };
    setScheduleData(nextData);
    return newLine;
  };

  const handleAddClaim = () => {
    createAndAddClaim();
  };

  const handleClaimChange = (claimLineId, field, value) => {
    if (!canEditSchedule || !scheduleData) return;
    const nextData = updateClaimLine(scheduleData, claimLineId, field, value);
    setScheduleData(nextData);
  };

  const handleRemoveClaim = (claimLineId) => {
    if (!canEditSchedule) return;
    setScheduleData((current) => removeClaimLine(current, claimLineId));
  };

  const handleRenumberClaims = () => {
    if (!canEditSchedule) return;
    setScheduleData((current) => renumberClaimLines(current));
  };

  const handleScheduleNameChange = (value) => {
    if (!canEditSchedule || value === schName) return;
    setSchName(value);
  };

  const handleNotesChange = (value) => {
    if (!canEditSchedule || value === notes) return;
    setNotes(value);
  };

  const handleReviewNotesChange = (value) => {
    if (!canEditSchedule || value === reviewNotes) return;
    setReviewNotes(value);
  };

  const prepareSave = () => {
    if (!canEditSchedule) return;
    const initializedScheduleData =
      scheduleData ||
      initializeSharedBasedPaymentData(scheduleDetails, {
        scheduleId: schedule?.id || "",
        yearOfAssessment: company?.yearOfAssessment || "",
        financialYearStart: company?.financialYearStart || "",
        financialYearEnd: company?.financialYearEnd || "",
      });
    const currentSchedule = {
      ...schedule,
      name: schName,
      notes,
      reviewNotes,
    };
    const currentCalculation =
      initializedScheduleData === scheduleData
        ? calculationResults
        : calculateSharedBasedPayment(initializedScheduleData);
    const currentDetails = createScheduleDetails(
      initializedScheduleData,
      currentCalculation,
      notes,
      reviewNotes
    );

    return { currentSchedule, currentDetails };
  };

  const performSave = ({ currentSchedule, currentDetails }) =>
    saveSchedule(currentSchedule, currentDetails);

  const handleSave = async () => {
    if (trackerLoading) return false;
    const pendingSave = prepareSave();
    if (!pendingSave) return false;

    if (saveValidationWarnings.length > 0) {
      setValidationWarning({
        open: true,
        pendingSave,
        loading: false,
      });
      return false;
    }

    return performSave(pendingSave);
  };

  const handleConfirmValidationSave = async () => {
    if (!validationWarning.pendingSave) return;

    setValidationWarning((current) => ({ ...current, loading: true }));
    const saved = await performSave(validationWarning.pendingSave);

    if (saved) {
      setValidationWarning({
        open: false,
        pendingSave: null,
        loading: false,
      });
      return;
    }

    setValidationWarning((current) => ({ ...current, loading: false }));
  };

  return (
    <Box sx={{ p: 4 }}>
      <ScheduleHeader
        title={schedule.type}
        companyName={company.name}
        companyType={company.companyType}
        uen={company.uen}
        yearOfAssessment={company.yearOfAssessment}
        financialYearStart={company.financialYearStart}
        financialYearEnd={company.financialYearEnd}
        currency={company.currency}
        handleSave={handleSave}
        handleExcel={() => setExcelDialogOpen(true)}
        deleteSchedule={deleteSchedule}
        canEditSchedule={canEditSchedule}
      >
        {scheduleData && (
          <SharedBasedPaymentPdf
            schedule={{ ...schedule, name: schName }}
            company={company}
            data={scheduleData}
            calculationResults={calculationResults}
            notes={notes}
            reviewNotes={reviewNotes}
          />
        )}
      </ScheduleHeader>

      <Box sx={{ mt: 3 }}>
        <ScheduleContainer
          key={schedule.id}
          id={schedule.id}
          schedule={schedule}
          schName={schName}
          taxAdjustment='Deduct'
          allowedToEditSchName
          isSourceApplicable
          sourceData={scheduleData?.sourceData || []}
          updateHeading={handleScheduleNameChange}
          canEditSchedule={canEditSchedule}
        >
          {() =>
            scheduleData && (
              <Stack spacing={3}>
                {excelImportSummary && (
                  <Alert
                    severity={
                      excelImportSummary.invalidCellsLeftBlank > 0
                        ? "warning"
                        : "success"
                    }
                    variant='outlined'
                    onClose={() => setExcelImportSummary(null)}
                    sx={{
                      alignItems: "flex-start",
                      "& .MuiAlert-message": { width: "100%", minWidth: 0 },
                    }}
                  >
                    <AlertTitle sx={{ mb: 0.5 }}>
                      Excel import completed
                    </AlertTitle>
                    <Typography variant='body2' color='text.secondary'>
                      {[
                        excelImportSummary.trackerImported
                          ? "Tracker transactions were replaced; share pool settings were retained"
                          : "Tracker transactions were retained",
                        excelImportSummary.qualifyingImported
                          ? "Matching deduction lines were updated; unmatched lines were retained"
                          : "Qualifying Deduction was retained",
                      ].join(". ")}
                      .
                    </Typography>
                    <Stack
                      direction='row'
                      useFlexGap
                      flexWrap='wrap'
                      gap={1}
                      sx={{ mt: 1.25 }}
                    >
                      {excelImportSummary.trackerImported && (
                        <Chip
                          size='small'
                          variant='outlined'
                          label={`Imported tracker transactions: ${excelImportSummary.trackerRows}`}
                        />
                      )}
                      {excelImportSummary.qualifyingImported && (
                        <Chip
                          size='small'
                          variant='outlined'
                          label={`Deduction lines: ${excelImportSummary.qualifyingUpdated} updated, ${excelImportSummary.qualifyingAdded} added`}
                        />
                      )}
                      {excelImportSummary.trackerEnabledAutomatically && (
                        <Chip
                          size='small'
                          color='success'
                          label='Tracker enabled automatically'
                        />
                      )}
                      {excelImportSummary.invalidCellsLeftBlank > 0 && (
                        <Chip
                          size='small'
                          color='warning'
                          label={`Invalid cells left blank: ${excelImportSummary.invalidCellsLeftBlank}`}
                        />
                      )}
                    </Stack>
                  </Alert>
                )}
                {/* 
                <SharedBasedPaymentValidationSummary
                  validation={calculationResults.validation}
                  data={scheduleData}
                  onOpenTracker={() => setActiveTab("tracker")}
                /> */}

                <SectionTabber
                  value={activeTab}
                  onTabChange={(_, value) => setActiveTab(value)}
                  tabsProps={{
                    variant: "scrollable",
                    scrollButtons: "auto",
                    allowScrollButtonsMobile: true,
                    "aria-label": "Share-based payment sections",
                  }}
                  tabs={[
                    {
                      value: "tracker",
                      label: "Acquired Shares Cost Tracker",
                      description: "Track share costs and linked transfers.",
                      chipLabel: scheduleData.useCostTracker ? "Active" : "Off",
                      chipColor: scheduleData.useCostTracker
                        ? "success"
                        : "error",
                      content: (
                        <SharesCostTracker
                          data={scheduleData}
                          calculationResults={calculationResults}
                          currency={company.currency}
                          canEdit={canEditSchedule}
                          trackerLoading={trackerLoading}
                          onTrackerToggle={handleTrackerToggle}
                          onOpenDeduction={() => setActiveTab("deduction")}
                          onChange={(updater) => {
                            if (!canEditSchedule) return;
                            setScheduleData(updater);
                          }}
                        />
                      ),
                    },
                    {
                      value: "deduction",
                      label: "Qualifying Deduction Schedule",
                      description: "Enter claim details and review deductions.",
                      badgeCount: scheduleData.claimLines.length,
                      content: (
                        <QualifyingDeductionTable
                          data={scheduleData}
                          calculationResults={calculationResults}
                          onChange={handleClaimChange}
                          onRemove={handleRemoveClaim}
                          onAdd={handleAddClaim}
                          onRenumber={handleRenumberClaims}
                          canEdit={canEditSchedule}
                        />
                      ),
                    },
                  ]}
                />
              </Stack>
            )
          }
        </ScheduleContainer>
      </Box>

      {scheduleData && (
        <Box sx={{ mt: 3 }}>
          <FurtherDeductionOutput output={calculationResults.output} />
        </Box>
      )}

      {scheduleData && (
        <SharedBasedPaymentExcelUpload
          data={scheduleData}
          open={excelDialogOpen}
          onClose={() => setExcelDialogOpen(false)}
          onApply={({ data: importedData, summary }) => {
            if (!canEditSchedule) return;
            setScheduleData(importedData);
            setExcelImportSummary(summary);
            setExcelDialogOpen(false);
          }}
        />
      )}

      <ValidationWarningDialog
        open={validationWarning.open}
        title='Save schedule with errors?'
        subtitle={`${saveValidationWarnings.length} validation ${
          saveValidationWarnings.length === 1
            ? "error requires"
            : "errors require"
        } attention.`}
        description='You can save the schedule, but the qualifying deduction output will remain 0 until these errors are resolved.'
        warnings={saveValidationWarnings}
        confirmLabel='Save anyway'
        cancelLabel='Review errors'
        loading={validationWarning.loading}
        onClose={() =>
          setValidationWarning({
            open: false,
            pendingSave: null,
            loading: false,
          })
        }
        onConfirm={handleConfirmValidationSave}
      />

      <Divider sx={{ my: 4 }} />

      <ScheduleNotes
        notes={notes}
        setNotes={handleNotesChange}
        reviewNotes={reviewNotes}
        setReviewNotes={handleReviewNotesChange}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default SharedBasedPaymentSchedule;
